/**
 * One-time `ort.env.wasm` initialisation.
 *
 * SIMD + multi-thread WebAssembly delivers a 2–4× speedup over the
 * scalar single-threaded variant on the same hardware. ort-web auto-
 * picks the best variant per browser, but only when its own env
 * options permit:
 *   - `numThreads` defaults to 1; we raise it to a sane CPU-count cap.
 *   - `simd` defaults to true (already), but we keep the explicit
 *     setting so a future ort-web release can't silently disable it.
 *
 * Multi-thread requires `crossOriginIsolated === true`, which is in
 * turn served by COOP=same-origin + COEP=credentialless headers
 * (see public/_headers). When the page is NOT cross-origin-isolated
 * we fall back to single-threaded SIMD without complaining.
 *
 * Speed-UP step #3 of 6 from the brownfield techniques review.
 */

import * as ort from 'onnxruntime-web';

let initialised = false;

export interface OrtWasmEnvDiagnostics {
  numThreads: number;
  simd: boolean;
  crossOriginIsolated: boolean;
}

export function ensureOrtWasmEnv(): OrtWasmEnvDiagnostics {
  // Idempotent init — `ort.env` is module-global; calling this from
  // every adapter is fine because the second call is a no-op.
  const xCoi =
    typeof self !== 'undefined' && (self as { crossOriginIsolated?: boolean }).crossOriginIsolated === true;
  const cpuCount = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 2 : 2;
  // Cap thread count: too many threads on phones causes thermal
  // spikes without proportional speedup. Empirically 4 is the
  // inflection point for ort-web on mid-range Android / iPhone.
  const targetThreads = xCoi ? Math.min(4, Math.max(1, cpuCount)) : 1;

  if (!initialised) {
    try {
      ort.env.wasm.simd = true;
      ort.env.wasm.numThreads = targetThreads;
      // proxy=true offloads inference to a dedicated worker. Keep it
      // OFF for now — combined with the previously-enabled COOP/COEP
      // headers it caused a regression on mobile Safari where the
      // proxy worker could not access cross-origin MediaPipe assets.
      // Revisit when we self-host all WASM dependencies under CORP.
      ort.env.wasm.proxy = false;
    } catch (err) {
      console.warn('[ort.env.wasm] init failed', err);
    }
    initialised = true;
  }
  return {
    numThreads: targetThreads,
    simd: true,
    crossOriginIsolated: xCoi,
  };
}
